import { localePath } from "../i18n";
import type { messages } from "../i18n";
import type { Locale, Messages } from "../i18n/types";
import { EXTERNAL_LINKS } from "../shared/external-links";
import { proxyCopy, proxyPath } from "../i18n/proxy";

export function el<T extends HTMLElement>(tag: string, className?: string, text?: string): T {
  const element = document.createElement(tag) as T;
  if (className) element.className = className;
  if (text) element.textContent = text;
  return element;
}

export function externalLink(text: string, href: string, className?: string): HTMLAnchorElement {
  const a = el<HTMLAnchorElement>("a", className, text);
  a.href = href;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  return a;
}

interface LocaleControlOption {
  locale: Locale;
  label: string;
  href?: string;
  className?: string;
}

function icon(paths: readonly string[], className: string): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", className);
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  for (const data of paths) {
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", data);
    svg.append(path);
  }
  return svg;
}

function globeIcon(): SVGSVGElement {
  const svg = icon(
    ["M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20", "M2 12h20"],
    "locale-icon"
  );
  const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
  circle.setAttribute("cx", "12");
  circle.setAttribute("cy", "12");
  circle.setAttribute("r", "10");
  svg.prepend(circle);
  return svg;
}

export function buildLocaleControl(
  currentLocale: Locale,
  copy: Messages,
  options: readonly LocaleControlOption[],
  onLocale?: (locale: Locale) => void
): HTMLElement {
  const control = el("div", "locale-control");
  const trigger = el<HTMLButtonElement>("button", "locale-trigger");
  trigger.type = "button";
  trigger.setAttribute("aria-label", copy.nav.language);
  trigger.setAttribute("aria-haspopup", "menu");
  trigger.setAttribute("aria-expanded", "false");
  trigger.append(
    globeIcon(),
    el(
      "span",
      "locale-code",
      currentLocale === "en"
        ? copy.nav.languageCodes.en
        : currentLocale === "zh-CN"
          ? copy.nav.languageCodes.zhCN
          : copy.nav.languageCodes.zhTW
    ),
    icon(["m6 9 6 6 6-6"], "locale-chevron")
  );

  const menu = el("div", "locale-menu");
  menu.id = "locale-menu";
  menu.setAttribute("role", "menu");
  menu.hidden = true;
  trigger.setAttribute("aria-controls", menu.id);
  const menuItems = options.map((option) => {
    const item = option.href
      ? el<HTMLAnchorElement>(
          "a",
          ["locale-option", option.className].filter(Boolean).join(" "),
          option.label
        )
      : el<HTMLButtonElement>("button", "locale-option", option.label);
    if (item instanceof HTMLButtonElement) item.type = "button";
    else item.href = option.href!;
    item.setAttribute("role", "menuitem");
    item.dataset.localeOption = option.locale;
    if (option.locale === currentLocale) item.setAttribute("aria-current", "page");
    menu.append(item);
    return item;
  });

  let outsideController: AbortController | null = null;
  const closeMenu = (restoreFocus = false): void => {
    menu.hidden = true;
    trigger.setAttribute("aria-expanded", "false");
    control.classList.remove("is-open");
    outsideController?.abort();
    outsideController = null;
    if (restoreFocus) trigger.focus();
  };
  const openMenu = (focus: "current" | "first" | "last" = "current"): void => {
    menu.hidden = false;
    trigger.setAttribute("aria-expanded", "true");
    control.classList.add("is-open");
    outsideController = new AbortController();
    document.addEventListener(
      "pointerdown",
      (event) => {
        if (!control.contains(event.target as Node)) closeMenu();
      },
      { capture: true, signal: outsideController.signal }
    );
    if (focus === "first") menuItems[0].focus();
    else if (focus === "last") menuItems.at(-1)?.focus();
    else
      (menuItems.find((item) => item.dataset.localeOption === currentLocale) ?? menuItems[0]).focus();
  };
  trigger.addEventListener("click", () => {
    if (menu.hidden) openMenu();
    else closeMenu();
  });
  trigger.addEventListener("keydown", (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      openMenu(event.key === "ArrowDown" ? "first" : "last");
    } else if (event.key === "Escape") {
      closeMenu(true);
    }
  });
  menu.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeMenu(true);
      return;
    }
    const current = menuItems.indexOf(document.activeElement as HTMLButtonElement | HTMLAnchorElement);
    let next = current;
    if (event.key === "ArrowDown") next = (current + 1) % menuItems.length;
    else if (event.key === "ArrowUp") next = (current - 1 + menuItems.length) % menuItems.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = menuItems.length - 1;
    else return;
    event.preventDefault();
    menuItems[next].focus();
  });
  menuItems.forEach((item, index) => {
    item.addEventListener("click", () => {
      const option = options[index];
      closeMenu(option.locale === currentLocale);
      if (!option.href) onLocale?.(option.locale);
    });
  });
  control.append(trigger, menu);
  return control;
}

export function buildArticleLocaleControl(
  copy: Messages,
  currentLocale: Locale,
  translationHref?: string
): HTMLElement | undefined {
  if (!translationHref) return undefined;
  const alternateLocale: Locale = currentLocale === "en" ? "zh-TW" : "en";
  const options: LocaleControlOption[] = [
    { locale: "en", label: copy.nav.languageNames.en },
    { locale: "zh-TW", label: copy.nav.languageNames.zhTW }
  ];
  const alternate = options.find((option) => option.locale === alternateLocale)!;
  alternate.href = translationHref;
  alternate.className = "article-language-link";
  return buildLocaleControl(currentLocale, copy, options);
}

export function buildSiteHeader(
  copy: typeof messages["zh-TW"],
  locale: Parameters<typeof localePath>[0],
  localeControl?: HTMLElement,
  options: {
    articlesHref?: string;
    articlesLabel?: string;
    esimLabel?: string;
  } = {}
): HTMLElement {
  const header = el("header", "site-header");
  const headerInner = el("div", "header-inner");

  const brand = el<HTMLAnchorElement>("a", "brand");
  brand.href = localePath(locale);
  brand.setAttribute("aria-label", copy.brand.name);
  brand.append(el("span", "brand-name", copy.brand.name));

  const nav = el("nav", "nav-links");
  nav.setAttribute("aria-label", copy.a11y?.menu || "Menu");

  const articlesLink = el<HTMLAnchorElement>(
    "a",
    "nav-link",
    options.articlesLabel || copy.nav?.articles || "科普文章"
  );
  articlesLink.href = options.articlesHref || (locale === "en" ? "/en/articles/" : "/articles/");

  const esimLink = externalLink(
    options.esimLabel || copy.nav?.esim || "自研 eSIM",
    EXTERNAL_LINKS.esim,
    "nav-link"
  );

  const proxyLink = el<HTMLAnchorElement>("a", "nav-link proxy-nav-link", proxyCopy[locale].nav);
  proxyLink.href = proxyPath(locale);
  if (location.pathname.replace(/\/$/u, "") === proxyPath(locale)) proxyLink.setAttribute("aria-current", "page");
  nav.append(proxyLink, articlesLink, esimLink);

  const actions = el("div", "header-actions");
  if (localeControl) {
    actions.append(localeControl);
  }

  // Mobile menu toggle button
  const mobileToggleBtn = el<HTMLButtonElement>("button", "mobile-menu-toggle");
  mobileToggleBtn.setAttribute("aria-label", "Toggle mobile menu");
  mobileToggleBtn.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-menu"><line x1="4" x2="20" y1="12" y2="12"></line><line x1="4" x2="20" y1="6" y2="6"></line><line x1="4" x2="20" y1="18" y2="18"></line></svg>
  `;
  actions.append(mobileToggleBtn);

  headerInner.append(brand, nav, actions);
  header.append(headerInner);

  // Mobile drawer
  const mobileDrawer = el("div", "mobile-menu-drawer");
  const drawerNav = el("nav", "mobile-nav-links");
  const mobileArticlesLink = articlesLink.cloneNode(true) as HTMLAnchorElement;
  const mobileEsimLink = esimLink.cloneNode(true) as HTMLAnchorElement;

  drawerNav.append(proxyLink.cloneNode(true), mobileArticlesLink, mobileEsimLink);
  mobileDrawer.append(drawerNav);
  header.append(mobileDrawer);

  let isMenuOpen = false;

  mobileToggleBtn.addEventListener("click", () => {
    isMenuOpen = !isMenuOpen;
    if (isMenuOpen) {
      mobileDrawer.classList.add("open");
      mobileToggleBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
      `;
    } else {
      mobileDrawer.classList.remove("open");
      mobileToggleBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-menu"><line x1="4" x2="20" y1="12" y2="12"></line><line x1="4" x2="20" y1="6" y2="6"></line><line x1="4" x2="20" y1="18" y2="18"></line></svg>
      `;
    }
  });

  return header;
}
